import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import './index.css';
import { Form } from '@formio/react';
import Loader from '../../../components/common/Loader';
import { Paper } from '../../../components/common/Containers';
import Typography from '../../../components/common/Typography';
import { Button } from '../../../components/common/Buttons';
import { useAlertContext } from '../../../contexts/AlertContext';
import { useTrans } from '../../../services/i18n';
import connect from '../connect';
import { useHistory } from 'react-router-dom';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import Box from '@material-ui/core/Box';

const RESPONSE_ID = 'formsAssignment';
const FormDetail = ({ getFormStackById, formSubmission, formPartialSubmission, downloadFormPdf, org }) => {
  const { id } = useParams();
  const { successAlert, errorAlert } = useAlertContext();
  const { trans } = useTrans();
  const history = useHistory();

  const [formDef, setFormDef] = useState(null);
  const [loading, setLoading] = useState(true);
  const [partialData, setPartialData] = useState({});
  const [initialSubmissionData, setInitialSubmissionData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingPartial, setIsSavingPartial] = useState(false);

  // Recursive function to find component by key in deeply nested structure
  const findComponentByKey = (components, targetKey) => {
    if (!Array.isArray(components)) return null;

    for (const component of components) {
      if (component.key === targetKey) {
        return component;
      }

      // Search recursively in nested components
      if (component.components) {
        const found = findComponentByKey(component.components, targetKey);
        if (found) return found;
      }

      // Search in columns (for grid/layout components)
      if (component.columns && Array.isArray(component.columns)) {
        for (const column of component.columns) {
          if (column.components) {
            const found = findComponentByKey(column.components, targetKey);
            if (found) return found;
          }
        }
      }
    }
    return null;
  };

  // Function to collect all field keys in the order they appear in formDef
  const collectFormFieldKeys = (components, depth = 0) => {
    const keys = [];
    if (!Array.isArray(components)) return keys;

    for (const component of components) {
      // Add field key if it has one and can store data (exclude layout and button components)
      if (
        component.key &&
        component.input !== false &&
        component.type !== 'button' &&
        component.type !== 'content' &&
        component.type !== 'htmlelement'
      ) {
        keys.push(component.key);
      }

      // Handle nested structures - process in depth-first order to maintain visual sequence

      // Handle columns layout first (they maintain visual left-to-right, top-to-bottom order)
      if (component.type === 'columns' && component.columns && Array.isArray(component.columns)) {
        for (const column of component.columns) {
          if (column.components) {
            keys.push(...collectFormFieldKeys(column.components, depth + 1));
          }
        }
      }
      // Handle fieldsets (they often contain related fields that should be grouped)
      else if (component.type === 'fieldset' && component.components) {
        keys.push(...collectFormFieldKeys(component.components, depth + 1));
      }
      // Handle panels (main content sections)
      else if (component.type === 'panel' && component.components) {
        keys.push(...collectFormFieldKeys(component.components, depth + 1));
      }
      // Handle all other nested components (catch-all for any other container types)
      else if (component.components) {
        keys.push(...collectFormFieldKeys(component.components, depth + 1));
      }
    }
    return keys;
  };

  const prepareFormSubmissionData = (formData = {}) => {
    // Create ordered data object based on formDef structure
    const orderedData = {};

    if (formDef?.components) {
      const fieldKeys = collectFormFieldKeys(formDef.components);

      // Add fields in the order they appear in formDef
      for (const key of fieldKeys) {
        if (formData.hasOwnProperty(key)) {
          orderedData[key] = formData[key];
        }
      }

      // Add any remaining fields that weren't found in formDef (edge case)
      const remainingFields = [];
      for (const [key, value] of Object.entries(formData)) {
        if (!orderedData.hasOwnProperty(key)) {
          orderedData[key] = value;
          remainingFields.push(key);
        }
      }

      // Debug logging for remaining fields
      if (
        (formDef.formUsfcrId === 'SAP' || formDef.name?.toLowerCase().includes('sap')) &&
        remainingFields.length > 0
      ) {
        console.log('SAP Form - Fields not found in formDef structure:', remainingFields);
      }
    } else {
      // Fallback to original spread if formDef is not available
      Object.assign(orderedData, formData);
    }

    if (org?.associatedSalesforceId) {
      orderedData.accountId = org.associatedSalesforceId;
    } else {
      console.error('Associated SalesforceId not found');
    }

    // Find formType component recursively and set FormType property
    if (formDef?.components) {
      const formTypeComponent = findComponentByKey(formDef.components, 'formType');
      if (formTypeComponent?.defaultValue) {
        orderedData.formType = formTypeComponent.defaultValue;
      }
    }

    return orderedData;
  };

  const onPartial = () => {
    // Prevent multiple submissions
    if (isSavingPartial) return;

    setIsSavingPartial(true);
    const enhancedPartialData = prepareFormSubmissionData(partialData);
    formPartialSubmission({
      formType: 'principal',
      formUsfcrId: formDef.formUsfcrId,
      formStackId: formDef._id,
      currentSection: '',
      fields: enhancedPartialData,
    })
      .then((res) => {
        successAlert(trans('form-progress-saved'));
        history.push('/client-portal-forms');
      })
      .catch((err) => {
        errorAlert(trans('error-saving-form'));
        setIsSavingPartial(false);
      });
  };

  useEffect(() => {
    setLoading(true);
    getFormStackById({ id, formType: 'principal' })
      .then((res) => {
        setFormDef(res.payload.form);
        const initialData = prepareFormSubmissionData(res.payload.form.partialSubmission || {});
        setInitialSubmissionData(initialData);
      })
      .catch(() => {
        errorAlert(trans('error-loading-form'));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, getFormStackById, errorAlert, trans, org?.associatedSalesforceId]);

  if (loading) {
    return <Loader open />;
  }
  if (!formDef) {
    return <Typography>{trans('form-not-found')}</Typography>;
  }

  return (
    <>
      <Loader open={isSubmitting || isSavingPartial} />
      <Paper style={{ padding: 24 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            backgroundColor: '#fff',
            paddingTop: 16,
            paddingBottom: 16,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            style={{ cursor: 'pointer' }}
            onClick={() => history.push('/client-portal-forms')}
          >
            <ArrowBackIcon fontSize="small" />
            <Typography variant="h6" style={{ marginLeft: 8 }}>
              {trans('cancel-and-quit')}
            </Typography>
          </Box>
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            {formDef.title}
          </Typography>


          <Button variant="contained" color="info" onClick={onPartial} style={{ minWidth: 160 }}>
            {trans('save-and-resume-later')}
          </Button>
        </Box>
        <div className="my-form-wrapper">
          <Form
            key={formDef._id + JSON.stringify(initialSubmissionData)}
            form={formDef}
            submission={{ data: initialSubmissionData }}
            options={{
              disableAlerts: false,
            }}
            onChange={({ data }) => setPartialData(data)}
            onSubmit={({ data }) => {
              // Prevent multiple submissions
              if (isSubmitting) {
                console.warn('Form is already being submitted, ignoring duplicate submission');
                return Promise.resolve();
              }

              setIsSubmitting(true);
              const enhancedSubmissionData = prepareFormSubmissionData(data);
              return formSubmission({
                formFields: enhancedSubmissionData,
                usfcr_form_id: formDef._id,
                formUsfcrId: formDef.formUsfcrId,
                type: 'principal',
              })
                .then((res) => {
                  if (res?.payload?.assignment) {
                    localStorage.setItem(RESPONSE_ID, JSON.stringify(res?.payload?.assignment));
                  }
                  successAlert(trans('form-submitted'));
                  history.push('/client-portal-forms');
                })
                .catch(() => {
                  errorAlert(trans('error-submitting-form'));
                  setIsSubmitting(false);
                });
            }}
            onError={(err) => {
              console.error(err);
              errorAlert(trans('error-submitting-form'));
            }}
          />
        </div>
      </Paper>
    </>
  );
};

export default connect(FormDetail);
